/**
 * Assessment sidebar - shows selected regulations and Run Impact Assessment button
 */

import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import queueIcon from "../../assets/icon-assessment-queue.svg";
import trashIcon from "../../assets/Trash.svg";
import styles from "../HomePage.module.css";
import { RunAiAssessmentMenuButton } from "../../components/RegulationsTable/RunAiAssessmentMenuButton";
import { useBrandThemeColors } from "../../branding";

const DOCK_VIEWPORT_MARGIN = 16;
const DRAG_CLICK_THRESHOLD_PX = 4;
const ASSESSMENT_QUEUE_DOCK_Z_INDEX = 1200;
const ASSESSMENT_QUEUE_PANEL_WIDTH = 588;
const ASSESSMENT_QUEUE_PANEL_GAP = 10;
const ASSESSMENT_QUEUE_PANEL_MAX_HEIGHT = 736;
const ASSESSMENT_QUEUE_PANEL_MIN_HEIGHT = 150;

type DragOffset = {
  x: number;
  y: number;
};

type DragState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startRect: DOMRect;
  moved: boolean;
};

type DragPointerEvent = {
  pointerId: number;
  clientX: number;
  clientY: number;
  preventDefault?: () => void;
};

type PanelPosition = {
  left: number;
  top: number;
  maxHeight: number;
};

function clampDockPosition(
  position: DragOffset,
  size: { width: number; height: number },
): DragOffset {
  const maxLeft = window.innerWidth - size.width - DOCK_VIEWPORT_MARGIN;
  const maxTop = window.innerHeight - size.height - DOCK_VIEWPORT_MARGIN;
  return {
    x: Math.min(
      Math.max(position.x, DOCK_VIEWPORT_MARGIN),
      Math.max(DOCK_VIEWPORT_MARGIN, maxLeft),
    ),
    y: Math.min(
      Math.max(position.y, DOCK_VIEWPORT_MARGIN),
      Math.max(DOCK_VIEWPORT_MARGIN, maxTop),
    ),
  };
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function computeAssessmentQueuePanelPosition(anchorRect: DOMRect): PanelPosition {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxPanelHeight = Math.min(
    ASSESSMENT_QUEUE_PANEL_MAX_HEIGHT,
    viewportHeight - 140,
  );

  const preferredLeft = anchorRect.right - ASSESSMENT_QUEUE_PANEL_WIDTH;
  const fallbackLeft = anchorRect.left;
  const left =
    preferredLeft >= DOCK_VIEWPORT_MARGIN
      ? preferredLeft
      : clampNumber(
          fallbackLeft,
          DOCK_VIEWPORT_MARGIN,
          viewportWidth - ASSESSMENT_QUEUE_PANEL_WIDTH - DOCK_VIEWPORT_MARGIN,
        );

  const topBelow = anchorRect.bottom + ASSESSMENT_QUEUE_PANEL_GAP;
  const availableAbove =
    anchorRect.top - ASSESSMENT_QUEUE_PANEL_GAP - DOCK_VIEWPORT_MARGIN;
  const availableBelow = viewportHeight - topBelow - DOCK_VIEWPORT_MARGIN;
  const shouldOpenAbove =
    availableAbove >= maxPanelHeight ||
    (availableAbove > availableBelow && availableBelow < maxPanelHeight);
  const maxHeight = Math.max(
    ASSESSMENT_QUEUE_PANEL_MIN_HEIGHT,
    Math.min(
      maxPanelHeight,
      shouldOpenAbove ? availableAbove : availableBelow,
    ),
  );
  const topAbove = anchorRect.top - ASSESSMENT_QUEUE_PANEL_GAP - maxHeight;
  const top = shouldOpenAbove
    ? topAbove
    : clampNumber(
        topBelow,
        DOCK_VIEWPORT_MARGIN,
        viewportHeight - maxHeight - DOCK_VIEWPORT_MARGIN,
      );

  return {
    left,
    top,
    maxHeight,
  };
}

function OverflowTitle({ text }: Readonly<{ text: string }>) {
  const [overflowed, setOverflowed] = useState(false);

  const checkOverflow = useCallback((el: HTMLSpanElement | null) => {
    if (el) setOverflowed(el.scrollWidth > el.clientWidth);
  }, []);

  return (
    <Tooltip title={text} disableHoverListener={!overflowed} arrow>
      <span ref={checkOverflow} className={styles.assessmentItemName}>
        {text}
      </span>
    </Tooltip>
  );
}

function formatAssessmentVersion(version: string): string {
  if (!version || version === "N/A" || version === "...") return version;
  const stripped =
    version.startsWith("v") || version.startsWith("V")
      ? version.substring(1).trim()
      : version;
  return `V ${stripped}`;
}

function splitTagValues(value: string): string[] {
  if (!value || value === "N/A" || value === "...") return [];
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of value.split(",")) {
    const trimmed = part.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    tags.push(trimmed);
  }
  return tags;
}

function formatAddedAt(iso?: string): string | null {
  if (!iso) return null;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : null;
}

interface AssessmentSidebarProps {
  assessmentSidebarIds: string[];
  assessmentSidebarSelectedIds?: string[];
  assessmentSidebarCollapsed: boolean;
  assessmentSidebarIdsCount: number;
  regulationTitles?: Record<string, string>;
  regulationVersions?: Record<string, string>;
  regulationMarkets?: Record<string, string>;
  regulationProductTypes?: Record<string, string>;
  regulationAddedAt?: Record<string, string>;
  onToggleCollapsed: () => void;
  onRemoveId: (id: string) => void;
  onClearAll: () => void;
  onToggleSelectedId?: (id: string) => void;
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  companyScopeStarting?: boolean;
}

export function AssessmentSidebar({
  assessmentSidebarIds,
  assessmentSidebarSelectedIds = assessmentSidebarIds,
  assessmentSidebarCollapsed,
  assessmentSidebarIdsCount,
  regulationTitles = {},
  regulationVersions = {},
  regulationMarkets = {},
  regulationProductTypes = {},
  regulationAddedAt = {},
  onToggleCollapsed,
  onRemoveId,
  onToggleSelectedId,
  onCompanyScopeImpact,
  onCustomScopeImpact,
  companyScopeStarting = false,
}: Readonly<AssessmentSidebarProps>) {
  const { primaryColor, primaryFontColor } = useBrandThemeColors();
  const selectedCount = assessmentSidebarSelectedIds.length;
  const canRunAutoImpactAssessment = selectedCount > 0;
  const iconWrapRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const removeDragListenersRef = useRef<(() => void) | null>(null);
  const suppressNextClickRef = useRef(false);
  const [dockPosition, setDockPosition] = useState<DragOffset | null>(null);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);

  const removeDragListeners = useCallback(() => {
    removeDragListenersRef.current?.();
    removeDragListenersRef.current = null;
  }, []);

  useEffect(() => () => removeDragListeners(), [removeDragListeners]);

  const updatePanelPosition = useCallback(() => {
    if (assessmentSidebarCollapsed) {
      setPanelPosition(null);
      return;
    }

    const anchorRect = iconWrapRef.current?.getBoundingClientRect();
    if (!anchorRect) return;

    const next = computeAssessmentQueuePanelPosition(anchorRect);
    setPanelPosition((previous) =>
      previous?.left === next.left &&
      previous.top === next.top &&
      previous.maxHeight === next.maxHeight
        ? previous
        : next,
    );
  }, [assessmentSidebarCollapsed]);

  useLayoutEffect(() => {
    updatePanelPosition();
  }, [
    updatePanelPosition,
    dockPosition,
    assessmentSidebarIds.length,
    assessmentSidebarCollapsed,
  ]);

  useEffect(() => {
    const handleResize = () => {
      if (dockPosition) {
        setDockPosition((previous) => {
          if (!previous) return previous;

          const rect = iconWrapRef.current?.getBoundingClientRect();
          const next = clampDockPosition(previous, {
            width: rect?.width ?? 80,
            height: rect?.height ?? 80,
          });

          return next.x === previous.x && next.y === previous.y ? previous : next;
        });
      }
      updatePanelPosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dockPosition, updatePanelPosition]);

  const updateDockPositionFromPointer = useCallback((event: DragPointerEvent) => {
    const dragState = dragStateRef.current;
    if (dragState?.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault?.();

    const deltaX = event.clientX - dragState.startClientX;
    const deltaY = event.clientY - dragState.startClientY;
    if (
      !dragState.moved &&
      (Math.abs(deltaX) > DRAG_CLICK_THRESHOLD_PX ||
        Math.abs(deltaY) > DRAG_CLICK_THRESHOLD_PX)
    ) {
      dragState.moved = true;
    }

    const nextPosition = clampDockPosition(
      {
        x: dragState.startRect.left + deltaX,
        y: dragState.startRect.top + deltaY,
      },
      {
        width: dragState.startRect.width,
        height: dragState.startRect.height,
      },
    );

    setDockPosition(nextPosition);
  }, []);

  const finishDockDrag = useCallback(
    (event: DragPointerEvent) => {
      const dragState = dragStateRef.current;
      if (dragState?.pointerId !== event.pointerId) {
        return;
      }

      suppressNextClickRef.current = dragState.moved;
      dragStateRef.current = null;
      removeDragListeners();
      updatePanelPosition();
    },
    [removeDragListeners, updatePanelPosition],
  );

  const handleIconPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button > 0) return;

      const iconWrap = iconWrapRef.current;
      if (!iconWrap) return;

      const startRect = iconWrap.getBoundingClientRect();

      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startRect,
        moved: false,
      };

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Window listeners below are the primary drag path; pointer capture is best-effort.
      }

      removeDragListeners();
      const handleWindowPointerMove = (nativeEvent: PointerEvent) => {
        updateDockPositionFromPointer(nativeEvent);
      };
      const handleWindowPointerUp = (nativeEvent: PointerEvent) => {
        finishDockDrag(nativeEvent);
      };
      window.addEventListener("pointermove", handleWindowPointerMove);
      window.addEventListener("pointerup", handleWindowPointerUp);
      window.addEventListener("pointercancel", handleWindowPointerUp);
      removeDragListenersRef.current = () => {
        window.removeEventListener("pointermove", handleWindowPointerMove);
        window.removeEventListener("pointerup", handleWindowPointerUp);
        window.removeEventListener("pointercancel", handleWindowPointerUp);
      };
    },
    [
      finishDockDrag,
      removeDragListeners,
      updateDockPositionFromPointer,
    ],
  );

  const handleIconClick = useCallback(() => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    onToggleCollapsed();
  }, [onToggleCollapsed]);

  const dock = (
    <aside
      className={styles.assessmentQueueDock}
      style={{
        position: "fixed",
        zIndex: ASSESSMENT_QUEUE_DOCK_Z_INDEX,
        ...(dockPosition
          ? {
              left: dockPosition.x,
              top: dockPosition.y,
              right: "auto",
              bottom: "auto",
            }
          : {
              right: 40,
              bottom: 40,
            }),
      }}
    >
      {!assessmentSidebarCollapsed && (
        <div
          className={styles.assessmentQueuePanel}
          style={{
            position: "fixed",
            ...(panelPosition
              ? {
                  left: panelPosition.left,
                  top: panelPosition.top,
                  right: "auto",
                  bottom: "auto",
                  maxHeight: panelPosition.maxHeight,
                }
              : {
                  right: 40,
                  bottom: 130,
                  maxHeight: `min(${ASSESSMENT_QUEUE_PANEL_MAX_HEIGHT}px, calc(100vh - 140px))`,
                }),
          }}
        >
          <div className={styles.assessmentContent}>
            <div
              className={styles.assessmentHeader}
              style={{ background: primaryColor }}
            >
              <img
                src={queueIcon}
                alt=""
                className={styles.assessmentHeaderIcon}
              />
              <div className={styles.assessmentHeaderText}>
                <span className={styles.assessmentHeaderTitleText}>
                  Selected For AI Assessment
                </span>
                <span className={styles.assessmentHeaderSubtitleText}>
                  Configure assessment concepts and select regulations to analyze
                </span>
              </div>
              <button
                type="button"
                onClick={onToggleCollapsed}
                className={styles.assessmentHeaderCloseButton}
                aria-label="Close assessment queue"
              >
                <CloseIcon
                  fontSize="small"
                  className={styles.assessmentHeaderCloseIcon}
                />
              </button>
            </div>

            <div className={styles.assessmentList}>
              {assessmentSidebarIds.length === 0 ? (
                <div className={styles.assessmentEmpty}>
                  No regulations added yet.
                </div>
              ) : (
                assessmentSidebarIds.map((id) => {
                  const detailLoaded = id in regulationMarkets;
                  const versionValue =
                    regulationVersions[id] || (detailLoaded ? "N/A" : "...");
                  const productTypeTags = detailLoaded
                    ? splitTagValues(regulationProductTypes[id] || "N/A")
                    : [];
                  const marketTags = detailLoaded
                    ? splitTagValues(regulationMarkets[id] || "N/A")
                    : [];
                  const addedAtLabel = formatAddedAt(regulationAddedAt[id]);
                  const title = regulationTitles[id] || id;

                  return (
                    <div key={id} className={styles.assessmentListItem}>
                      <Checkbox
                        size="small"
                        checked={assessmentSidebarSelectedIds.includes(id)}
                        onChange={() => onToggleSelectedId?.(id)}
                        className={styles.assessmentListItemCheckbox}
                        sx={{
                          color: "#D1D5DB",
                          "&.Mui-checked": { color: primaryColor },
                        }}
                      />
                      <div className={styles.assessmentItem}>
                        <div className={styles.assessmentItemMain}>
                          <OverflowTitle text={title} />
                          <span className={styles.assessmentItemId}>{id}</span>
                          <div className={styles.assessmentItemTags}>
                            <span className={styles.assessmentItemTag}>
                              {formatAssessmentVersion(versionValue)}
                            </span>
                            {detailLoaded ? (
                              <>
                                {productTypeTags.map((tag, index) => (
                                  <span
                                    key={`${id}-pt-${index}-${tag}`}
                                    className={styles.assessmentItemTag}
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {marketTags.map((tag, index) => (
                                  <span
                                    key={`${id}-m-${index}-${tag}`}
                                    className={styles.assessmentItemTag}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </>
                            ) : (
                              <span className={styles.assessmentItemTag}>
                                ...
                              </span>
                            )}
                          </div>
                        </div>

                        <div className={styles.assessmentItemDivider}>
                          <div className={styles.assessmentItemDividerLine} />
                        </div>

                        <div className={styles.assessmentItemFooter}>
                          <span className={styles.assessmentItemAddedAt}>
                            {addedAtLabel
                              ? `${addedAtLabel} Added`
                              : "Added"}
                          </span>
                          <button
                            type="button"
                            onClick={() => onRemoveId(id)}
                            className={styles.assessmentItemRemoveButton}
                            aria-label={`Remove ${id}`}
                          >
                            <img
                              src={trashIcon}
                              alt=""
                              className={styles.assessmentItemRemoveIcon}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className={styles.assessmentFooter}>
              <Button
                variant="outlined"
                onClick={onToggleCollapsed}
                className={styles.assessmentFooterCancelButton}
                sx={{
                  borderColor: `${primaryColor} !important`,
                  color: `${primaryColor} !important`,
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <RunAiAssessmentMenuButton
                variant="toolbar"
                disabled={!canRunAutoImpactAssessment}
                companyScopeStarting={companyScopeStarting}
                onCompanyScopeImpact={onCompanyScopeImpact}
                onCustomScopeImpact={onCustomScopeImpact}
                buttonSx={{
                  backgroundColor: `${primaryColor} !important`,
                  color: `${primaryFontColor} !important`,
                  "&:hover": {
                    backgroundColor: `${primaryColor} !important`,
                    color: `${primaryFontColor} !important`,
                  },
                  "& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label":
                    {
                      color: `${primaryFontColor} !important`,
                    },
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div ref={iconWrapRef} className={styles.assessmentQueueIconWrap}>
        <button
          type="button"
          className={styles.assessmentQueueIconButton}
          onPointerDown={handleIconPointerDown}
          onClick={handleIconClick}
          aria-label="Open assessment queue"
        >
          <img
            src={queueIcon}
            alt=""
            draggable={false}
            className={styles.assessmentQueueIcon}
          />
          <span className={styles.assessmentQueueIconBadge}>
            {assessmentSidebarIdsCount}
          </span>
        </button>
      </div>
    </aside>
  );

  return createPortal(dock, document.body);
}
